import {
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  MailOutlined,
  MedicineBoxOutlined,
  PhoneOutlined,
  UserOutlined,
} from "@ant-design/icons"
import { Button, Col, Form, Input, Row, Select } from "antd"
import { Rule } from "antd/es/form"
import { useState } from "react"

import AnimateInFlex, { ANIMATION_BY_KEY } from "#blocks/AnimateInFlex"
import CopyToClipboard from "#blocks/CopyToClipboard"
import CreatableOptionSelect from "#blocks/CreatableOptionSelect"
import Flex from "#blocks/Flex"
import { SFormHeader } from "#blocks/Form/formComponents"
import TagIcons from "#blocks/TagIcons"
import { Student } from "#classes/Student"
import { USER_ROLE_OPTIONS } from "#constants/userOptions"
import { useUserContext } from "#contexts/UserContextProvider"
import {
  RESOURCES,
  USER_ACTIONS,
  USER_ROLE_COLORS,
  USER_ROLES,
  UserRole,
} from "#customTypes/composedTypes/authTypes"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import {
  AddUserFormValues,
  STAFF_TITLES,
} from "#customTypes/composedTypes/userTypes"
import { ReactChild } from "#customTypes/globalTypes"
import AvatarForm from "#forms/AddStudent/AvatarForm"
import useHasPermission from "#hooks/newHooks/useHasPermission"
import useErrorModal from "#hooks/useErrorModal"
import { isEmailFieldKey, normalizeEmailFormValue } from "#utils/emailHelpers"
import { canAssignRole } from "#utils/permissionsHelpers"
import { camelToWords, ErrorType } from "#utils/textFormatters"

const { Option } = Select

const InfoField = ({
  label,
  value,
  canCopy,
}: {
  label: string | ReactChild
  value: string | string[] | ReactChild
  canCopy?: boolean
}) => {
  if (!value || (Array.isArray(value) && !value.length)) return null

  return (
    <AnimateInFlex
      animation={ANIMATION_BY_KEY.leftSm}
      style={{ fontSize: 12, alignItems: "flex-start" }}
    >
      <Flex style={{ fontWeight: "bold", width: "40%", height: 20 }}>
        {label}
      </Flex>
      <Flex col style={{ maxWidth: "60%" }}>
        {Array.isArray(value) ? (
          value.map((item, index) => <span key={index}>{item}</span>)
        ) : canCopy ? (
          <CopyToClipboard
            dataGetter={() => value}
            label={value as string}
            compact
          />
        ) : (
          value
        )}
      </Flex>
    </AnimateInFlex>
  )
}

const InfoDisplay = ({
  initialValues,
}: {
  initialValues: AddUserFormValues
}) => {
  const form = Form.useFormInstance<AddUserFormValues>()
  const canSeeContactInfo = useHasPermission(
    RESOURCES.USER,
    USER_ACTIONS.VIEW_CONTACT_INFO
  )

  const getValue = (field: keyof AddUserFormValues): string => {
    return (initialValues?.[field] as string) ?? ""
  }

  const tags = <TagIcons values={initialValues.tags} />
  const allergies = <TagIcons values={initialValues.allergies} />
  const address = Student.formatAddress(form.getFieldValue("primaryAddress"))
  const roleColor =
    USER_ROLE_COLORS[getValue("role") as keyof typeof USER_ROLE_COLORS]

  return (
    <>
      <Flex center col gap={8}>
        <AvatarForm entityId={initialValues.id} fieldName="avatar" size={128} />
        <h2>
          <Flex gap={8}>
            {getValue("title") ? `${getValue("title")} ` : null}
            {getValue("name")}
            {tags}
          </Flex>
        </h2>
        <h3>{getValue("dob")}</h3>
        <span style={{ color: roleColor, fontWeight: "bold" }}>
          {camelToWords(getValue("role"))}
        </span>
      </Flex>

      <SFormHeader>Basic Info</SFormHeader>
      <InfoField
        label={
          <Flex gap={8}>
            <MedicineBoxOutlined /> Allergies
          </Flex>
        }
        value={allergies}
      />
      <InfoField
        label={
          <Flex gap={8}>
            <UserOutlined /> Gender
          </Flex>
        }
        value={camelToWords(getValue("gender"))}
      />

      <InfoField
        label={
          <Flex gap={8}>
            <FileTextOutlined /> Notes
          </Flex>
        }
        value={getValue("notes") ?? "No notes"}
      />

      {canSeeContactInfo && (
        <>
          <SFormHeader>Contact Info</SFormHeader>

          <Flex col gap={8}>
            <InfoField
              canCopy
              label={
                <Flex gap={8}>
                  <PhoneOutlined /> Phone
                </Flex>
              }
              value={getValue("personalPhone")}
            />
            <InfoField
              canCopy
              label={
                <Flex gap={8}>
                  <MailOutlined /> Email
                </Flex>
              }
              value={getValue("personalEmail")}
            />
          </Flex>

          <AnimateInFlex
            animation={ANIMATION_BY_KEY.leftSm}
            style={{
              marginTop: 16,
              alignItems: "flex-start",
              justifyContent: "space-between",
              fontSize: 12,
            }}
          >
            <Flex
              gap={8}
              style={{ height: 20, fontWeight: "bold", width: "40%" }}
            >
              <EnvironmentOutlined /> Address
            </Flex>

            <Flex style={{ width: "60%" }} gap={8}>
              {address}
              <CopyToClipboard data={address} compact />
            </Flex>
          </AnimateInFlex>
        </>
      )}
    </>
  )
}

const InfoEditField = ({
  name,
  label,
  rules,
  span,
}: {
  name: string | string[]
  label: string
  rules?: Rule[]
  span?: number
}) => {
  const fieldKey = Array.isArray(name) ? name[name.length - 1] : name
  const isEmail = isEmailFieldKey(fieldKey)

  return (
    <Col span={span}>
      <AnimateInFlex
        animation={ANIMATION_BY_KEY.leftSm}
        style={{ width: "100%" }}
      >
        <Form.Item
          name={name}
          label={label}
          rules={rules}
          style={{ width: "100%" }}
          normalize={isEmail ? normalizeEmailFormValue : undefined}
        >
          <Input placeholder={label} type={isEmail ? "email" : undefined} />
        </Form.Item>
      </AnimateInFlex>
    </Col>
  )
}

const InfoEdit = ({ targetUserRole }: { targetUserRole?: UserRole }) => {
  const canSeeContactInfo = useHasPermission(
    RESOURCES.USER,
    USER_ACTIONS.VIEW_CONTACT_INFO
  )
  const canManageRoles = useHasPermission(RESOURCES.USER, USER_ACTIONS.UPDATE)
  const { effectiveRole } = useUserContext()

  const userId = Form.useWatch("id")
  const assignableRoleOptions = USER_ROLE_OPTIONS.filter(({ value }) =>
    canAssignRole(effectiveRole, value as UserRole)
  )
  const canEditThisUsersRole = canAssignRole(
    effectiveRole,
    targetUserRole ?? USER_ROLES.DISABLED
  )

  return (
    <>
      <Flex center col gap={8}>
        <AvatarForm entityId={userId} fieldName="avatar" size={128} />
      </Flex>

      <SFormHeader>Basic Info</SFormHeader>

      <Row gutter={16}>
        <InfoEditField
          name="name"
          label="Display name (used as the main name)"
          span={24}
        />
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <AnimateInFlex animation={ANIMATION_BY_KEY.leftSm}>
            <Form.Item name="title" label="Title" style={{ width: "100%" }}>
              <Select
                allowClear
                placeholder="Mr, Ms, or Mrs"
                options={STAFF_TITLES.map((value) => ({
                  value,
                  label: value,
                }))}
              />
            </Form.Item>
          </AnimateInFlex>
        </Col>
        <InfoEditField
          name="firstName"
          label="First name"
          rules={[{ required: true, message: "Please input a first name!" }]}
          span={24}
        />
        <InfoEditField
          name="lastName"
          label="Last name"
          rules={[{ required: true, message: "Please input a last name!" }]}
          span={24}
        />

        <InfoEditField
          name="dob"
          label="Date of birth"
          rules={[
            { required: true, message: "Please select a date of birth!" },
          ]}
          span={12}
        />
        <Col span={12}>
          <AnimateInFlex animation={ANIMATION_BY_KEY.leftSm}>
            <Form.Item name="gender" label="Gender" style={{ width: "100%" }}>
              <Select placeholder="Gender" showSearch>
                <Option value="male">Male</Option>
                <Option value="female">Female</Option>
                <Option value="other">Other</Option>
              </Select>
            </Form.Item>
          </AnimateInFlex>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={24}>
          <AnimateInFlex animation={ANIMATION_BY_KEY.leftSm}>
            <Form.Item name="role" label="Role" style={{ width: "100%" }}>
              <Select
                placeholder="Select role"
                showSearch
                disabled={!canManageRoles || !canEditThisUsersRole}
              >
                {assignableRoleOptions.map(({ value, label }) => {
                  const color =
                    USER_ROLE_COLORS[value as keyof typeof USER_ROLE_COLORS]
                  return (
                    <Option key={value} value={value} style={{ color }}>
                      {label}
                    </Option>
                  )
                })}
              </Select>
            </Form.Item>
          </AnimateInFlex>
        </Col>
      </Row>

      <SFormHeader>Notes</SFormHeader>
      <Row gutter={16}>
        <Col span={24}>
          <AnimateInFlex animation={ANIMATION_BY_KEY.leftSm}>
            <Form.Item name="notes" style={{ width: "100%" }}>
              <Input.TextArea placeholder="Enter notes here" rows={6} />
            </Form.Item>
          </AnimateInFlex>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={24}>
          <SFormHeader>Allergies</SFormHeader>
          <AnimateInFlex animation={ANIMATION_BY_KEY.leftSm}>
            <Form.Item name="allergies" style={{ width: "100%" }}>
              <CreatableOptionSelect
                docType={DOC_TYPES.ALLERGIES}
                placeholder="Select or add allergies"
              />
            </Form.Item>
          </AnimateInFlex>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={24}>
          <SFormHeader>Tags</SFormHeader>
          <AnimateInFlex animation={ANIMATION_BY_KEY.leftSm}>
            <Form.Item name="tags" style={{ width: "100%" }}>
              <CreatableOptionSelect
                docType={DOC_TYPES.TAGS}
                placeholder="Select or add tags"
              />
            </Form.Item>
          </AnimateInFlex>
        </Col>
      </Row>

      {canSeeContactInfo && (
        <>
          <SFormHeader>Contact Info</SFormHeader>
          <Row gutter={16}>
            <InfoEditField
              name="personalPhone"
              label="Personal phone"
              span={24}
            />

            <InfoEditField
              name="personalEmail"
              label="Personal email"
              span={24}
            />
          </Row>

          <SFormHeader>Primary Address</SFormHeader>
          <Row gutter={16}>
            <InfoEditField
              name={["primaryAddress", "street1"]}
              label="Street 1"
              span={24}
            />
            <InfoEditField
              name={["primaryAddress", "street2"]}
              label="Street 2"
              span={24}
            />
            <InfoEditField
              name={["primaryAddress", "city"]}
              label="City"
              span={24}
            />
            <InfoEditField
              name={["primaryAddress", "state"]}
              label="State"
              span={8}
            />
            <InfoEditField
              name={["primaryAddress", "zipCode"]}
              label="Zip code"
              span={16}
            />
          </Row>
        </>
      )}
    </>
  )
}

const UserInfo = ({ initialValues }: { initialValues: AddUserFormValues }) => {
  const form = Form.useFormInstance<AddUserFormValues>()
  const [isEditing, setIsEditing] = useState(!initialValues?.id)
  const [isSaving, setIsSaving] = useState(false)

  const canEditUser = useHasPermission(RESOURCES.USER, USER_ACTIONS.UPDATE)
  const openErrorModal = useErrorModal("saving a user")

  const handleSave = async () => {
    const now = Date.now()
    const timeout = 500
    setIsSaving(true)
    try {
      await form.submit()
    } catch (error) {
      console.error("Error saving user:", error)
      openErrorModal(
        error as ErrorType,
        `User: ${initialValues?.name} (${initialValues?.id})`
      )
    } finally {
      // Force 500ms saving time for user confidence
      const timeRemaining = timeout - (Date.now() - now)
      await new Promise((resolve) => setTimeout(resolve, timeRemaining))
      setIsSaving(false)
    }
  }

  return (
    <>
      <Flex
        style={{
          justifyContent: "flex-end",
          paddingBottom: 16,
          paddingRight: 30,
          height: 48,
          borderRight: "1px solid #ccc",
        }}
        gap={8}
      >
        {isEditing && canEditUser ? (
          <>
            <Button
              style={{ fontSize: 18, backgroundColor: "green" }}
              icon={<CheckOutlined />}
              loading={isSaving}
              onClick={handleSave}
              shape="circle"
              type="primary"
            />
            <Button
              style={{ fontSize: 18, backgroundColor: "red" }}
              icon={<CloseOutlined />}
              loading={isSaving}
              onClick={() => setIsEditing(false)}
              type="primary"
            />
          </>
        ) : (
          canEditUser && (
            <Button
              style={{ fontSize: 18 }}
              icon={<EditOutlined />}
              onClick={() => setIsEditing(true)}
              type="text"
            />
          )
        )}
      </Flex>
      <Flex
        col
        style={{
          borderRight: "1px solid #ccc",
          paddingRight: 30,
          overflowY: "auto",
          maxHeight: 1000,
        }}
        gap={20}
      >
        {isEditing ? (
          <InfoEdit targetUserRole={initialValues?.role} />
        ) : (
          <InfoDisplay initialValues={initialValues} />
        )}
      </Flex>
    </>
  )
}

export default UserInfo
